import React, { useMemo } from 'react';
import { formatMoney } from 'accounting';
import sanitize from 'utils/sanitize';
import { byId } from 'utils/common';

import canvases from 'domains/canvases';
import { useAppDispatch, useAppSelector } from 'utils/hooks';
import {
  selectCatalogsLoaded,
  selectCustomPriceLoading,
  selectOptionsLoaded,
  selectSubcatalogsLoaded,
} from 'selectors/loading_states';
import activeEditor from 'domains/active_editor';
import { selectOptionPrices } from 'selectors/prices';

import type { OptionAPIData, OptionGroupAPIData } from 'domains/option_groups';
import type { OptionalPair } from 'types/common_types';

type PriceToolOptionSelectProps = {
  optionGroup: OptionGroupAPIData;
};

type PriceToolOptionProps = {
  optionGroupId: number;
  option: OptionAPIData;
};

function PriceToolOptionSelect(props: PriceToolOptionSelectProps) {
  const { optionGroup } = props;
  const isRequired = optionGroup.required;

  const dispatch = useAppDispatch();
  const catalogsLoaded = useAppSelector(selectCatalogsLoaded);
  const subcatalogsLoaded = useAppSelector(selectSubcatalogsLoaded);
  const optionsLoaded = useAppSelector(selectOptionsLoaded);
  const activeOptionConfig = useAppSelector(activeEditor.selectors.optionConfig);
  const optionPrices = useAppSelector(selectOptionPrices);
  const customPriceLoading = useAppSelector(selectCustomPriceLoading);

  function handleChangeOption(value: string) {
    const selectionId = value === '' ? undefined : parseInt(value);
    const optionPair: OptionalPair<number, number> = [optionGroup.id, selectionId];
    const payload = { options: [optionPair] };
    dispatch(canvases.actions.changeActiveCanvasConfig.options(payload));
  }

  const selectedOption = useMemo(() => {
    const optionId = activeOptionConfig[optionGroup.id];
    return optionGroup.options.find(byId(optionId));
  }, [activeOptionConfig, optionGroup]);

  const optionGroupPrice = useMemo(() => {
    return optionPrices[optionGroup.id] || 0;
  }, [optionPrices, optionGroup.id]);

  return (
    <div key={optionGroup.uid} className="PriceTool__input">
      <label htmlFor={`${optionGroup.name}-select`}>
        <span dangerouslySetInnerHTML={{ __html: optionGroup.name }} />
        {optionGroupPrice > 0 && (
          <span
            className="PriceTool__input__price"
            style={customPriceLoading ? { opacity: '0.25' } : {}}
          >
            + {formatMoney(optionGroupPrice)}
          </span>
        )}
      </label>
      <select
        id={`${optionGroup.name}-select`}
        value={selectedOption ? selectedOption.id : 'default'}
        disabled={!catalogsLoaded || !subcatalogsLoaded || !optionsLoaded || customPriceLoading}
        onChange={(e) => handleChangeOption(e.target.value)}
      >
        {!isRequired && <option value="">None</option>}
        {optionGroup.options.map((option) => (
          <PriceToolOption key={option.id} optionGroupId={optionGroup.id} option={option} />
        ))}
      </select>
    </div>
  );
}

function PriceToolOption(props: PriceToolOptionProps) {
  const { optionGroupId, option } = props;

  const activeOptionConfig = useAppSelector(activeEditor.selectors.optionConfig);
  const disabledOptions = useAppSelector(activeEditor.selectors.disabledOptions);

  if (!option) return null;

  const priceDisplay = useMemo(() => {
    const isSelected = activeOptionConfig[optionGroupId] === option.id;
    if (!isSelected && parseFloat(option.service.price) > 0) {
      return formatMoney(option.service.price);
    }

    return null;
  }, [activeOptionConfig, optionGroupId, option]);

  const isDisabled = useMemo(() => {
    return !!disabledOptions[option.id] || option.read_only;
  }, [disabledOptions, option]);

  const disabledBy = useMemo(() => {
    if (disabledOptions[option.id]) {
      return `Not available with ${sanitize(disabledOptions[option.id].by)}`;
    }

    return '';
  }, [disabledOptions, option.id]);

  return (
    <option
      key={option.id}
      value={option.id || 'default'}
      disabled={isDisabled}
      title={isDisabled ? disabledBy : ''}
    >
      {sanitize(option.name)}
      {priceDisplay && `  +${priceDisplay}`}
    </option>
  );
}

export default PriceToolOptionSelect;
